<template>
  <v-col>
    <v-row>
      <v-col>
        <span class="subtitle-2 font-weight-bold">Users</span>
      </v-col>

      <v-col class="col-auto">
        <v-btn
          color="primary"
          @click="showDialogToAddUser = true"
        >
          Add User
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th
                  v-for="header in userHeaders"
                  :key="header.text"
                >
                  {{ header.text }}
                </th>

                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="tableRow in userDocs"
                :key="tableRow.id"
              >
                <td>{{ tableRow.displayName }}</td>
                <td>{{ tableRow.email }}</td>
                <td>{{ tableRow.access ? tableRow.access.role : 'error' }}</td>

                <td>
                  <v-btn
                    v-for="action in actions"
                    :key="action.text"
                    @click="promptAction(action.value, tableRow)"
                  >
                    <v-icon>{{ action.icon }}</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <!-- "Add User" Dialog -->
    <v-dialog
      v-model="showDialogToAddUser"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          <span class="subtitle-2 font-weight-bold">Add User</span>
        </v-card-title>

        <v-card-text>
          <v-form>
            <v-text-field
              v-model="userForm.displayName"
              label="Display Name"
              required
            />

            <v-text-field
              v-model="userForm.email"
              label="Email"
              required
            />

            <v-select
              v-model="userForm['access.role']"
              :items="['external', 'internal']"
              label="Role"
              required
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            @click="createUser"
          >
            Add User
          </v-btn>

          <v-btn
            color="primary"
            @click="showDialogToAddUser = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import { createObject } from '@/utils/objects'
import server from '@/store/server'

const { mapState: mapAuthState } = createNamespacedHelpers('auth')

export default {
  name: 'AdminUser',
  data() {
    return {
      showDialogToAddUser: false,
      showViewToUpdateUser: false,
      showViewToUpdatePassword: false,

      didUpdatePassword: false,
      errorUpdatingPassword: '',
      successUpdatingPassword: false,

      userHeaders: [
        {
          sortable: true,
          text: 'Display Name',
          value: 'personName',
        },
        {
          sortable: true,
          text: 'Email',
          value: 'email',
        },
        {
          sortable: true,
          text: 'Role',
          value: 'access.role',
        },
      ],

      actions: [
        {
          text: 'Edit',
          value: 'editUser',
          icon: 'pencil',
        },
        {
          text: 'Update Password',
          value: 'updatePassword',
          icon: 'lock',
        },
        {
          text: 'Delete',
          value: 'deleteUser',
          icon: 'delete',
        },
      ],
    }
  },
  computed: {
    ...mapAuthState({
      tenantId: (state) => state.tenantId,

      $user: (state) => state.user,
      $userDoc: (state) => state.userDoc,
      $userDocs: (state) => state.userDocs,
      $userForm: (state) => state.userForm,
    }),

    user: {
      get() {
        return this.$user
      },
      set(value) {
        this.$store.commit('auth/user', value)
      },
    },
    userDoc: {
      get() {
        return this.$userDoc
      },
      set(value) {
        this.$store.commit('auth/userDoc', value)
      },
    },
    userDocs: {
      get() {
        return this.$userDocs
      },
      set(value) {
        this.$store.commit('auth/userDocs', value)
      },
    },
    userForm: {
      get() {
        return this.$userForm
      },
      set(value) {
        this.$store.commit('auth/userForm', value)
      },
    },
  },
  created() {
    this.fetchUsersFromFirebase()
  },
  methods: {
    async fetchUsersFromFirebase() {
      this.userDocs = await server.getUsers(this.tenantId)
    },

    async createUser() {
      const { userForm } = this

      // Create a new user.
      return server.createUser(userForm).then((res) => {
        if (!res) {
          // Inform the user creation was unsuccessful.
          return this.handleAuthError({ code: '403', layer: 'app', message: 'Error Creating User', status: 'error' })
        }

        if (res.error) {
          // Inform the user creation was unsuccessful.
          return this.handleAuthError(res)
        }

        // Inform the user creation was successful.
        this.showDialogToAddUser = false

        return res
      }).catch((error) => {
        // Set the error message for the "Add User" dialog to display.
        this.errorCreatingUser = error.message || 'Error creating user. If the problem persists, please contact support.'

        // Render the error message in the "Add User" dialog.
        this.didCreateUser = true
      })
    },

    async editUser(user) {
      return user
    },

    async updateUser(user) {
      return user
    },

    async deleteUser(user) {
      return user
    },

    promptToUpdateUser() {
      const { user } = this

      this.userForm = createObject(user, {
        actionType: 'update',
        objectType: 'user',
        tenantId: this.tenantId,
      })

      // Open a view that allows the user to update their information.
      this.showViewToUpdateUser = true
    },

    promptToUpdatePassword() {
      const { user } = this

      this.userForm = createObject(user, {
        actionType: 'update',
        objectType: 'password',
        tenantId: this.tenantId,
      })

      // Open a view that allows the user to update their password.
      this.showViewToUpdatePassword = true
    },

    updateAuthPassword() {
      const { inputEmail, inputPassword, tenantId } = this
      const url = window.location.href

      // Update the user's password.
      return this.updatePassword({ email: inputEmail, password: inputPassword, tenantId, url }).then((res) => {
        if (!res) {
          // Inform the user the password update was unsuccessful.
          return this.handleAuthError({ code: '403', layer: 'app', message: 'Error Updating Password', status: 'error' })
        }

        if (res.error) {
          // Inform the user the password update was unsuccessful.
          return this.handleAuthError(res)
        }

        // Inform the user the password update was successful.
        this.successUpdatingPassword = true

        return res
      }).catch((error) => {
        // Set the error message for the "Update Password" view to display.
        this.errorUpdatingPassword = error.message || 'Error updating password. If the problem persists, please contact support.'

        // Render the error message in the "Update Password" view.
        this.didUpdatePassword = true
      })
    },

    promptAction(action, user) {
      switch (action) {
        case 'editUser':
          this.editUser(user)
          break
        case 'deleteUser':
          this.deleteUser(user)
          break
        case 'updatePassword':
          this.updateAuthPassword()
          break
        case 'updateUser':
          this.updateUser(user)
          break
        default:
          // @todo Warn the user that the action is not supported.
          break
      }
    },
  },
}
</script>
